import { socialPlatforms } from '@app/modules/social-export/domain/platforms';
import { theme as appTheme } from '@mi-front/ui';
import { Box } from '@mui/material';

const COMPLETED = 'COMPLETED';

const ICON_SIZES = {
  small: { pill: 16, popover: 24 },
  medium: { pill: 24, popover: 32 },
} as const;

export type UploadedPlatformsStatusSize = 'small' | 'medium';

export type UploadedPlatformStatusItem = {
  id: string;
  platform: string;
  status: string;
};

const SocialIconWrapper = ({
  children,
  size = 24,
}: {
  children: React.ReactNode;
  size?: number;
}) => (
  <Box
    sx={{
      width: size,
      height: size,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& svg': { fontSize: size * 0.9 },
    }}
  >
    {children}
  </Box>
);

export interface UploadedPlatformsStatusProps {
  items: readonly UploadedPlatformStatusItem[];
  /** Inline card row: smaller icons, red when not completed. Popover: larger icons, orange when not completed. */
  variant: 'pill' | 'popover';
  size?: UploadedPlatformsStatusSize;
  gap?: number;
}

/**
 * Row of social platform icons colored by upload outcome (completed vs not).
 */
export function UploadedPlatformsStatus({
  items,
  variant,
  size = 'small',
  gap = variant === 'popover' ? 1 : 0.25,
}: UploadedPlatformsStatusProps) {
  const iconSize = ICON_SIZES[size][variant === 'pill' ? 'pill' : 'popover'];
  const incompleteColor = variant === 'pill' ? appTheme.red : appTheme.orange;

  return (
    <Box display="flex" alignItems="center" flexWrap="wrap" gap={gap}>
      {items.map((item) => {
        try {
          const platform = socialPlatforms.get(item.platform);
          const isCompleted = item.status === COMPLETED;
          const iconColor = isCompleted ? appTheme.greenDark : incompleteColor;
          return (
            <Box key={item.id}>
              <SocialIconWrapper size={iconSize}>
                {platform.Icon({
                  sx: { color: iconColor },
                })}
              </SocialIconWrapper>
            </Box>
          );
        } catch {
          return null;
        }
      })}
    </Box>
  );
}
